import { ChevronDown, ChevronUp, Eye, Factory, Leaf, Pencil, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Control, UseFormHandleSubmit, UseFormSetValue, useWatch } from "react-hook-form";
import { useGetProcessTypesWholeList } from "../../api/cultivations";
import { NEW_FACILITY_KEY, NewFacilityOption } from "../../config/constants";
import { IngredientDataTypesOption } from "../../config/enums/IngredientType.enum";
import { AccordionTypes } from "../../helpers/AccordionHelper";
import ProcessHeatSource from "../../screens/cultivation/processing/ProcessHeatSource";
import ProcessWaterSource from "../../screens/cultivation/processing/ProcessWaterSource";
import FacilityRegisterModal from "../../screens/facility/facilityRegister/FacilityRegisterModal";
import { validateCharacterLength } from "../../utils/utils";
import MeasurementDetails from "../measurementDetails/MeasurementDetails";
import { CustomButton } from "../ui/button/CustomButton/CustomButton";
import { Badge } from "../ui/common/badge";
import { Button } from "../ui/common/button";
import { Card, CardContent, CardHeader } from "../ui/common/card";
import { FormInput } from "../ui/Input/FormInput/FormInput";
import { FormSelect } from "../ui/Input/FormSelector/FormSelect";
import { FormSelectWithButton } from "../ui/Input/FormSelectWithButton";

interface FacilityOption {
  value: string;
  text: string;
}

interface DataTypeOption {
  value: string | number;
  text: string;
}

interface ProcessingDetailsProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  facilityWholeList: FacilityOption[];
  dataTypeOptions: DataTypeOption[];
  disabled?: boolean;
  setVisibleFacilityDetailsModal: (visible: boolean) => void;
  refetchFacilityList?: () => void | Promise<any>;
  setViewIngredient: (visible: boolean) => void;
  data: any;
  accordionType: AccordionTypes | null;
  handleClick: (type: AccordionTypes) => void;
  lockStatus: boolean;
  deleteLockStatus?: boolean;
  handleSubmit: UseFormHandleSubmit<any>;
  handleFormSubmit: (data: any) => void;
  isValid: boolean;
  setVisibleDeleteModal?: (visible: boolean) => void;
}

const ProcessingDetails = ({
  control,
  setValue,
  facilityWholeList,
  dataTypeOptions,
  disabled = false,
  setVisibleFacilityDetailsModal,
  refetchFacilityList,
  setViewIngredient,
  data,
  accordionType,
  handleClick,
  lockStatus,
  deleteLockStatus = lockStatus,
  handleSubmit,
  handleFormSubmit,
  isValid,
  setVisibleDeleteModal
}: ProcessingDetailsProps): JSX.Element => {
  const facilityName = useWatch({ control, name: "facilityName" });
  const dataType = useWatch({ control, name: "dataType" });
  const processingName = useWatch({ control, name: "processingName" });

  // Set default dataType to SPECIFIC if empty
  const effectiveDataType = dataType || IngredientDataTypesOption.SPECIFIC;

  const { data: processTypesWholeList } = useGetProcessTypesWholeList(effectiveDataType);

  const [showFacilityRegister, setShowFacilityRegister] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const hideContentGenericStatus = useMemo(() => {
    return effectiveDataType === IngredientDataTypesOption.SPECIFIC;
  }, [effectiveDataType]);

  // Set default dataType in form if empty
  useEffect(() => {
    if (!dataType) {
      setValue("dataType", IngredientDataTypesOption.SPECIFIC, { shouldDirty: false });
    }
  }, [dataType, setValue]);

  // Determine saved state based on existing process data from props
  useEffect(() => {
    if (data?.processingName) {
      setIsSaved(true);
      setIsEditing(false);
      setIsOpen(false);
    } else {
      setIsSaved(false);
      setIsEditing(true);
      setIsOpen(true);
    }
  }, [data?.processingName]);

  const facilityOptionsWithNew = useMemo(() => {
    const list = facilityWholeList || [];
    return [...list, NewFacilityOption];
  }, [facilityWholeList]);

  const readOnly = isSaved && !isEditing;

  const handleEdit = () => {
    if (lockStatus) return;
    setIsEditing(true);
    setIsOpen(true);
  };

  const handleSaveConfiguration = async (formData: any): Promise<void> => {
    await handleFormSubmit(formData);
    setIsSaved(true);
    setIsEditing(false);
    setIsOpen(false);
  };

  const handleFacilityChange = (value: string) => {
    if (value === NEW_FACILITY_KEY) {
      setShowFacilityRegister(true);
      return;
    }
    setValue("facilityName", value, { shouldDirty: true });
  };

  const handleFacilityModalClose = (open: boolean, data?: { newFacilityId: string }): void => {
    setShowFacilityRegister(open);
    if (data?.newFacilityId) {
      if (refetchFacilityList) {
        const result = refetchFacilityList();
        if (result && typeof result.then === "function") {
          result
            .then(() => {
              setValue("facilityName", data.newFacilityId, { shouldDirty: true });
            })
            .catch(() => {
              // Silently handle refetch errors
              setValue("facilityName", data.newFacilityId, { shouldDirty: true });
            });
        } else {
          setValue("facilityName", data.newFacilityId, { shouldDirty: true });
        }
      } else {
        setValue("facilityName", data.newFacilityId, { shouldDirty: true });
      }
    }
  };

  const getSummaryText = (): string => {
    if (!isSaved || !processingName) {
      return "Complete all required fields (*) to unlock sub-sections";
    }
    const parts = [];
    if (processingName) parts.push(processingName);
    if (dataType) parts.push(dataType);
    if (facilityName) {
      const facility = facilityWholeList?.find(f => f.value === facilityName);
      parts.push(facility?.text || facilityName);
    }
    return parts.join(" · ");
  };

  return (
    <>
      <Card className="tw-border-border !tw-p-0">
        <CardHeader
          className="tw-bg-primary/10 tw-py-3.5 tw-px-3.5 tw-rounded-t-lg tw-cursor-pointer"
          onClick={() => setIsOpen(prev => !prev)}
        >
          <div className="tw-flex tw-items-center tw-gap-2">
            <Factory className="tw-h-4 tw-w-4 tw-text-primary" />
            <div className="tw-flex-1">
              <p className="tw-text-sm tw-font-semibold tw-text-foreground">
                Process Configuration
              </p>
              <p className="tw-text-sm tw-text-muted-foreground tw-mt-0.5">{getSummaryText()}</p>
            </div>
            {isSaved && (
              <Badge
                variant="outline"
                className="tw-bg-success/10 tw-text-success tw-border-success/30 tw-text-xs"
              >
                Saved
              </Badge>
            )}
            {isSaved && !isEditing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={e => {
                  e.stopPropagation();
                  handleEdit();
                }}
                disabled={lockStatus}
                className="tw-gap-1 tw-text-xs tw-h-7"
              >
                <Pencil className="tw-h-3 tw-w-3" /> Edit
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={e => {
                e.stopPropagation();
                setVisibleDeleteModal?.(true);
              }}
              disabled={deleteLockStatus}
              className="tw-pointer-events-auto tw-gap-1 tw-text-xs tw-h-7 tw-text-destructive hover:tw-bg-destructive/10 hover:tw-text-destructive"
            >
              <Trash2 className="tw-h-3 tw-w-3" /> Delete
            </Button>

            {isOpen ? (
              <ChevronUp className="tw-h-4 tw-w-4 tw-text-muted-foreground tw-shrink-0" />
            ) : (
              <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground tw-shrink-0" />
            )}
          </div>
        </CardHeader>
        {isOpen && (
          <CardContent
            className={`tw-px-3.5 tw-pt-3.5 tw-pb-3.5 ${
              readOnly || lockStatus ? "tw-opacity-85 tw-pointer-events-none" : ""
            }`}
          >
            <div className="tw-space-y-4">
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                <div>
                  <FormInput
                    control={control}
                    name={"processingName"}
                    label="Processing Name"
                    placeholder="Processing Name"
                    required
                    errorMessage="Please enter processing Name"
                    rules={{
                      validate: value =>
                        validateCharacterLength(
                          value,
                          32,
                          "Processing name cannot exceed 32 characters"
                        )
                    }}
                  />
                </div>
                <div>
                  <FormSelect
                    control={control}
                    name={`dataType`}
                    label="Data Type"
                    placeholder="Select a data type"
                    required={true}
                    errorMessage="Please select a data type"
                    options={dataTypeOptions}
                  />
                </div>
                <div>
                  <FormSelect
                    control={control}
                    name={`processingType`}
                    label="Processing Type"
                    placeholder="Select a processing type"
                    errorMessage="Please select a processing type"
                    options={processTypesWholeList}
                  />
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <div className="tw-relative">
                  <FormSelectWithButton
                    control={control}
                    name="facilityName"
                    label="Facility Name"
                    placeholder="Select Facility Name"
                    options={facilityOptionsWithNew}
                    required
                    errorMessage="Facility name is required"
                    disabled={disabled}
                    onChange={handleFacilityChange}
                    buttonOnClick={() => setVisibleFacilityDetailsModal(true)}
                    buttonDisabled={!facilityName}
                    buttonIcon={<Eye size={16} />}
                    buttonTheme="grey"
                    buttonOutLine
                  />
                </div>
                {hideContentGenericStatus ? (
                  <FormInput
                    control={control}
                    name={"machineModelNo"}
                    label="Machine Model Number"
                    placeholder="Machine Model Number"
                    errorMessage="Please enter machine model number"
                  />
                ) : null}
              </div>
              {hideContentGenericStatus ? (
                <>
                  <MeasurementDetails control={control} lockStatus={lockStatus || readOnly} />
                  <div className="tw-w-full tw-flex tw-flex-col tw-mt-2 tw-gap-6">
                    <ProcessHeatSource
                      control={control}
                      setValue={setValue}
                      lockStatus={lockStatus || readOnly}
                    />
                    <ProcessWaterSource
                      control={control}
                      setValue={setValue}
                      lockStatus={lockStatus || readOnly}
                    />
                  </div>
                </>
              ) : null}
              {!readOnly && (
                <div className="tw-flex tw-items-center tw-justify-between tw-pt-3.5 tw-mt-3.5 tw-border-t tw-border-border">
                  {!isValid && (
                    <p className="tw-text-xs tw-text-muted-foreground">
                      Fill all required fields (*) to save
                    </p>
                  )}
                  <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
                    {isEditing && (
                      <CustomButton
                        onClick={() => {
                          setIsEditing(false);
                          setIsOpen(false);
                        }}
                        disabled={!isValid}
                        iconSize="small"
                        className="tw-gap-1.5 tw-py-2.5 !tw-bg-transparent tw-border tw-border-border tw-text-foreground hover:tw-bg-muted/50"
                        title={"Cancel"}
                        theme="lightgrey"
                      />
                    )}
                    <CustomButton
                      onClick={handleSubmit(handleSaveConfiguration)}
                      disabled={!isValid}
                      iconSize="small"
                      className="tw-gap-1.5 tw-py-2.5"
                      title={isSaved && isEditing ? "Update Configuration" : "Save Configuration"}
                      theme="green"
                    />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {showFacilityRegister && (
        <FacilityRegisterModal
          visibleModal={showFacilityRegister}
          setVisibleModal={handleFacilityModalClose}
          refetchFacilityList={refetchFacilityList}
        />
      )}
    </>
  );
};

export default ProcessingDetails;
